import { useTranslation } from '@pancakeswap/localization'
import { Currency, CurrencyAmount, Percent, TradeType } from '@pancakeswap/sdk'
import { LegacyPair as Pair } from '@pancakeswap/smart-router/legacy-router'
import { Modal, ModalV2, QuestionHelper } from '@pancakeswap/uikit'
import { formatAmount } from '@pancakeswap/utils/formatFractions'
import { useState, memo } from 'react'
import { RoutingSettingsButton } from '@/components/Menu/GlobalSettings/SettingsModal'
import { Field } from '@/state/swap/actions'
import Link from 'next/link'
import FormattedPriceImpact from './FormattedPriceImpact'
import { RouterViewer } from './RouterViewer'
import SwapRoute from './SwapRoute'
import { SlippageTooltip } from '@/components/tooltips/slippage'
import { PriceImpactTooltip } from '@/components/tooltips/price-impact'
import { TradingFeeTooltip } from '@/components/tooltips/trading-fee'
import { Typography } from '@/components/ui/typography'

export const TradeSummary = memo(function TradeSummary({
  inputAmount,
  outputAmount,
  tradeType,
  slippageAdjustedAmounts,
  priceImpactWithoutFee,
  realizedLPFee,
  isMM = false,
}: {
  hasStablePair?: boolean
  inputAmount?: CurrencyAmount<Currency>
  outputAmount?: CurrencyAmount<Currency>
  tradeType?: TradeType
  slippageAdjustedAmounts: {
    INPUT?: CurrencyAmount<Currency>
    OUTPUT?: CurrencyAmount<Currency>
  }
  priceImpactWithoutFee?: Percent
  realizedLPFee?: CurrencyAmount<Currency>
  isMM?: boolean
}) {
  const { t } = useTranslation()
  const isExactIn = tradeType === TradeType.EXACT_INPUT

  return (
    <div className={'flex flex-col gap-1 text-sm'}>
      <div className={'flex justify-between'}>
        <div className={'flex gap-1'}>
          <Typography variant={'muted'}>{isExactIn ? t('Minimum received') : t('Maximum sold')}</Typography>
          <SlippageTooltip />
        </div>
        <div>
          {isExactIn
            ? `${formatAmount(slippageAdjustedAmounts[Field.OUTPUT], 4)} ${outputAmount?.currency?.symbol}` ?? '-'
            : `${formatAmount(slippageAdjustedAmounts[Field.INPUT], 4)} ${inputAmount?.currency?.symbol}` ?? '-'}
        </div>
      </div>
      {priceImpactWithoutFee && (
        <div className={'flex justify-between'}>
          <div className={'flex gap-1'}>
            <Typography variant={'muted'}>{t('Price Impact')}</Typography>
            <PriceImpactTooltip />
          </div>

          {isMM ? <div>--</div> : <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />}
        </div>
      )}

      {realizedLPFee && (
        <div className={'flex items-center justify-between'}>
          <div className={'flex gap-1'}>
            <Typography variant={'muted'}>{t('Trading Fee')}</Typography>
            <TradingFeeTooltip isMM={isMM} />
          </div>
          <div>{`${formatAmount(realizedLPFee, 4)} ${inputAmount?.currency?.symbol}`}</div>
        </div>
      )}
    </div>
  )
})

export interface AdvancedSwapDetailsProps {
  hasStablePair?: boolean
  pairs?: Pair[]
  path?: Currency[]
  priceImpactWithoutFee?: Percent
  realizedLPFee?: CurrencyAmount<Currency>
  slippageAdjustedAmounts?: {
    INPUT?: CurrencyAmount<Currency>
    OUTPUT?: CurrencyAmount<Currency>
  }
  inputAmount?: CurrencyAmount<Currency>
  outputAmount?: CurrencyAmount<Currency>
  tradeType?: TradeType
  isMM?: boolean
}

export const AdvancedSwapDetails = memo(function AdvancedSwapDetails({
  pairs,
  path,
  priceImpactWithoutFee,
  realizedLPFee,
  slippageAdjustedAmounts,
  inputAmount,
  outputAmount,
  tradeType,
  hasStablePair,
  isMM = false,
}: AdvancedSwapDetailsProps) {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const showRoute = Boolean(path && path.length > 1)
  return (
    <div>
      {inputAmount && (
        <>
          <TradeSummary
            inputAmount={inputAmount}
            outputAmount={outputAmount}
            tradeType={tradeType}
            slippageAdjustedAmounts={slippageAdjustedAmounts ?? {}}
            priceImpactWithoutFee={priceImpactWithoutFee}
            realizedLPFee={realizedLPFee}
            hasStablePair={hasStablePair}
            isMM={isMM}
          />
          {showRoute && (
            <>
              <div>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <div className={'text-sm'}>{t('MM Route')}</div>
                  <QuestionHelper
                    text={t(
                      'The Market Maker (MM) route is automatically selected for your trade to achieve the best price for your trade.',
                    )}
                    ml="4px"
                    placement="top"
                  />
                </span>
                {path && <SwapRoute path={path} />}
                <ModalV2 closeOnOverlayClick isOpen={isModalOpen} onDismiss={() => setIsModalOpen(false)}>
                  <Modal
                    title={
                      <div className={'flex justify-center'}>
                        {t('Route')}{' '}
                        <QuestionHelper
                          text={t(
                            'Route is automatically calculated based on your routing preference to achieve the best price for your trade.',
                          )}
                          ml="4px"
                          placement="top"
                        />
                      </div>
                    }
                    onDismiss={() => setIsModalOpen(false)}
                  >
                    <RouterViewer
                      isMM={isMM}
                      inputCurrency={inputAmount?.currency}
                      pairs={pairs}
                      path={path}
                      outputCurrency={outputAmount?.currency}
                    />
                    <div className={'mt-2 flex justify-center'}>
                      <RoutingSettingsButton />
                    </div>
                  </Modal>
                </ModalV2>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
})
